import { useEffect, useRef } from 'react';
import { useEscapeClose } from '../../hooks/useEscapeClose';

interface EnterAllBidsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Feature 022 — the "Enter all bids to proceed" popup shown when the learner activates the disabled
 * "add" control before every bid box is filled (spec FR-015). Dismissible with Esc via the shared
 * `useEscapeClose` hook (Constitution VI).
 */
export function EnterAllBidsDialog({ isOpen, onClose }: EnterAllBidsDialogProps) {
  const okButtonRef = useRef<HTMLButtonElement>(null);

  useEscapeClose(onClose, isOpen);

  useEffect(() => {
    if (isOpen) okButtonRef.current?.focus();
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="enter-all-bids-title"
    >
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-card rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
        <p id="enter-all-bids-title" className="text-ink mb-6">
          Enter all bids to proceed
        </p>
        <div className="flex justify-end">
          <button
            ref={okButtonRef}
            type="button"
            onClick={onClose}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
